// src/LocalRiverInfo.js
import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { functions, httpsCallable } from './firebase.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const rivers = [
  { name: 'Chattooga River', siteId: '02177000' },
  { name: 'Nantahala River', siteId: '03505550' },
  { name: 'Cheoah River', siteId: '0351706800' },
  { name: 'Tuckaseegee River', siteId: '03513000' },
  { name: 'Valley River', siteId: '03550000' },
];

const colors = ['rgba(75,192,192,1)', 'rgba(255,99,132,1)', 'rgba(54,162,235,1)', 'rgba(255,206,86,1)', 'rgba(153,102,255,1)'];

function LocalRiverInfo({ user, isAdmin, isMobile }) {
  const [riverData, setRiverData] = useState({});
  const [selectedRivers, setSelectedRivers] = useState(rivers.map(r => r.siteId));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const formatRiverRow = (row) => {
      const levels = (row.levels || []).map((point) => ({
        time: new Date(point.time).toLocaleString(),
        value: point.value,
      }));
      const flows = (row.flows || []).map((point) => ({
        time: new Date(point.time).toLocaleString(),
        value: point.value,
      }));
      const latestLevel = row.latestLevel
        ? { time: new Date(row.latestLevel.time), value: row.latestLevel.value }
        : null;
      const latestFlow = row.latestFlow
        ? { time: new Date(row.latestFlow.time), value: row.latestFlow.value }
        : null;

      return {
        name: row.name,
        siteId: row.siteId,
        levels,
        flows,
        latestLevel,
        latestFlow,
      };
    };

    const fetchRiverData = async () => {
      setLoading(true);
      setError(null);
      try {
        const getRiverData = httpsCallable(functions, 'getRiverData');
        const result = await getRiverData({});
        const payload = result.data?.rivers || {};
        const newData = {};
        for (const river of rivers) {
          const row = payload[river.siteId];
          if (row) {
            newData[river.siteId] = formatRiverRow(row);
          }
        }
        if (!Object.keys(newData).length) {
          throw new Error('No river readings returned from USGS');
        }
        setRiverData(newData);
      } catch (err) {
        console.error('Error fetching river data:', err);
        setError(err.message || 'Failed to load river data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchRiverData();
  }, []);

  const handleSelectionChange = (siteId) => {
    setSelectedRivers(prev => 
      prev.includes(siteId) ? prev.filter(id => id !== siteId) : [...prev, siteId]
    );
  };

  const buildChartData = (type) => {
    const labels = new Set();
    selectedRivers.forEach(siteId => {
      riverData[siteId]?.[type].forEach(point => labels.add(point.time));
    });
    const sortedLabels = Array.from(labels).sort((a, b) => new Date(a) - new Date(b));

    const datasets = selectedRivers.map((siteId, index) => {
      const river = rivers.find(r => r.siteId === siteId);
      const points = riverData[siteId]?.[type] || [];
      const dataMap = new Map(points.map(p => [p.time, p.value]));
      return {
        label: `${river.name} ${type === 'levels' ? 'Gage Height (ft)' : 'Discharge (cfs)'}`,
        data: sortedLabels.map(label => dataMap.get(label) || null),
        borderColor: colors[index % colors.length],
        backgroundColor: colors[index % colors.length].replace('1)', '0.2)'),
        yAxisID: 'y',
        tension: 0.1,
        spanGaps: true,
      };
    });

    return {
      labels: sortedLabels,
      datasets,
    };
  };

  const commonChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    elements: {
      point: {
        radius: 1.5,
        hoverRadius: 5,
        borderWidth: 2,
      },
      line: {
        borderWidth: 3,
        tension: 0.25,
      },
    },
    plugins: {
      legend: {
        position: 'top',
        align: 'end',
        labels: {
          usePointStyle: true,
          padding: 20,
          font: { size: 13, weight: 500 },
          color: '#334155',
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(15, 23, 42, 0.95)',
        borderColor: '#0ea5e9',
        borderWidth: 1,
        titleFont: { size: 13, weight: 600 },
        bodyFont: { size: 14 },
        padding: 12,
        displayColors: true,
      },
    },
    scales: {
      x: {
        grid: { color: '#e2e8f0', lineWidth: 0.5, tickLength: 6 },
        borderColor: '#cbd5e1',
        ticks: {
          autoSkip: true,
          maxTicksLimit: 10,
          color: '#64748b',
          font: { size: 12 },
        },
      },
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        grid: { color: '#e2e8f0', lineWidth: 0.5 },
        borderColor: '#cbd5e1',
        ticks: { 
          color: '#64748b', 
          font: { size: 12 },
          padding: 8,
        },
      },
    },
  };

  const levelOptions = {
    ...commonChartOptions,
    plugins: {
      ...commonChartOptions.plugins,
      title: {
        display: false,
      },
    },
    scales: {
      ...commonChartOptions.scales,
      y: {
        ...commonChartOptions.scales.y,
        title: {
          display: true,
          text: 'Gage Height (ft)',
          color: '#475569',
        },
      },
    },
  };

  const flowOptions = {
    ...commonChartOptions,
    plugins: {
      ...commonChartOptions.plugins,
      title: {
        display: false,
      },
    },
    scales: {
      ...commonChartOptions.scales,
      y: {
        ...commonChartOptions.scales.y,
        title: {
          display: true,
          text: 'Discharge (cfs)',
          color: '#475569',
        },
      },
    },
  };

  if (loading) {
    return (
      <div className="river-page">
        <div className="river-hero">
          <h1>Local River Information</h1>
          <p>Real-time USGS data for rivers around the Nantahala area</p>
        </div>
        <div className="river-loading">Loading live river data...</div>
      </div>
    );
  }

  if (error) {
    return <div className="river-page"><div className="river-error">{error}</div></div>;
  }

  const levelData = buildChartData('levels');
  const flowData = buildChartData('flows');

  const selectedRiverDetails = selectedRivers.map(siteId => {
    const river = rivers.find(r => r.siteId === siteId);
    const data = riverData[siteId];
    return { ...river, ...data };
  });

  return (
    <div className="river-page">
      {/* Hero */}
      <div className="river-hero">
        <div className="river-hero-content">
          <h1>Local River Information</h1>
          <p>
            Live USGS streamflow and gage data for the rivers that matter most to our community.
            Perfect for paddlers, anglers, and anyone who loves these waters.
          </p>
        </div>
      </div>

      {/* River Selector */}
      <div className="river-section">
        <div className="section-header">
          <h2>Select Rivers to Display</h2>
          <span className="river-count">{selectedRivers.length} of {rivers.length} selected</span>
        </div>
        
        <div className="river-selector">
          {rivers.map(river => {
            const isSelected = selectedRivers.includes(river.siteId);
            return (
              <button
                key={river.siteId}
                className={`river-chip ${isSelected ? 'selected' : ''}`}
                onClick={() => handleSelectionChange(river.siteId)}
              >
                {river.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Current Conditions */}
      {selectedRiverDetails.length > 0 && (
        <div className="river-section">
          <div className="section-header">
            <h2>Current Conditions</h2>
            <span className="last-updated">Last 7 days • Updates every 15 minutes</span>
          </div>

          <div className="current-conditions-grid">
            {selectedRiverDetails.map((river, index) => {
              const level = river.latestLevel?.value;
              const flow = river.latestFlow?.value;
              
              return (
                <div key={river.siteId} className="condition-card">
                  <div className="condition-header">
                    <h3>{river.name}</h3>
                    <div className="river-color-dot" style={{ backgroundColor: colors[index % colors.length] }} />
                  </div>
                  
                  <div className="condition-stats">
                    <div className="stat">
                      <div className="stat-label">Gage Height</div>
                      <div className="stat-value">
                        {level ? level.toFixed(2) : '—'} <span className="unit">ft</span>
                      </div>
                    </div>
                    <div className="stat">
                      <div className="stat-label">Flow</div>
                      <div className="stat-value">
                        {flow ? Math.round(flow).toLocaleString() : '—'} <span className="unit">cfs</span>
                      </div>
                    </div>
                  </div>

                  <a 
                    href={`https://waterdata.usgs.gov/monitoring-location/${river.siteId}/`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="usgs-link"
                  >
                    View full USGS data →
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Charts */}
      <div className="river-section">
        <div className="section-header">
          <h2>7-Day Trends</h2>
        </div>

        <div className="charts-grid">
          <div className="chart-card">
            <div className="chart-header">
              <h3>Gage Height (ft)</h3>
              <span className="chart-subtitle">Water level above a fixed point</span>
            </div>
            <div className="chart-wrapper">
              <Line data={levelData} options={levelOptions} />
            </div>
          </div>

          <div className="chart-card">
            <div className="chart-header">
              <h3>Discharge (cfs)</h3>
              <span className="chart-subtitle">Volume of water flowing past the gauge</span>
            </div>
            <div className="chart-wrapper">
              <Line data={flowData} options={flowOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* Info Footer */}
      <div className="river-footer">
        <p>
          Data provided by the <a href="https://waterdata.usgs.gov/" target="_blank" rel="noopener noreferrer">U.S. Geological Survey (USGS)</a>.
          Values update every 15 minutes. Always check official sources before heading out on the water.
        </p>
      </div>
    </div>
  );
}

export default LocalRiverInfo;